"use client";

import { useContext, useEffect, useState } from "react";
import { useTrackShipment } from "@/hooks/useTrackShipment";
import { TrackShipmentStart } from "./track-shipment-start";
import { ShipmentTrackingDetails } from "./shipment-tracking-details";
import TrackShipmentSkeleton from "./track-shipment-skeleton";
import { TrackerContext } from "./hero";

export function TrackShipment({ shipmentId = "" }: { shipmentId?: string }) {
  const { setShowDetails: setCtxShowDetails, setShowExpandedHistory } = useContext(TrackerContext);
  const [trackingId, setTrackingId] = useState(() => shipmentId);
  const [localError, setLocalError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(shipmentId.length > 0);

  useEffect(() => {
    setCtxShowDetails(showDetails);
  }, [showDetails, setCtxShowDetails]);
  const [initiateTracking, setInitiateTracking] = useState(true);
  const [shouldScrollToStart, setShouldScrollToStart] = useState(false);

  const trackQuery = useTrackShipment({
    queryParams: { shipmentId: trackingId.trim() },
    enabled: trackingId.trim().length > 0 && initiateTracking,
    retry: false,
  });

  useEffect(() => {
    if (shipmentId.trim() === "") {
      setTrackingId("");
      setShowDetails(false);
      setLocalError(null);
    }
  }, [shipmentId]);

  useEffect(() => {
    if (!shouldScrollToStart || showDetails) return;

    const trackStartElement = document.getElementById("track-start");
    if (trackStartElement) {
      trackStartElement.scrollIntoView({ behavior: "smooth" });
      setShouldScrollToStart(false);
    }
  }, [shouldScrollToStart, showDetails]);

  const handleTrack = async () => {
    setInitiateTracking(true);
    setShowExpandedHistory(false);
    const value = trackingId.trim();
    if (!value) {
      setLocalError("Please enter a tracking ID.");
      return;
    }

    setLocalError(null);
    const result = await trackQuery.refetch();

    if (result.data?.data) {
      setShowDetails(true);
    } else {
      setShowDetails(false);
    }
  };

  const reset = () => {
    setTrackingId("");
    setShowDetails(false);
    setShowExpandedHistory(false);
    setInitiateTracking(false);
    setLocalError(null);
    setShouldScrollToStart(true);
  };

  const errorMessage =
    localError ||
    (trackQuery.isError
      ? "We couldn't find a shipment with that ID. Please try again."
      : undefined);

  return (
    <div className="flex flex-col w-full gap-6 pb-2">
      {trackQuery.isFetching ? (
        <TrackShipmentSkeleton />
      ) : showDetails && trackQuery.data?.data ? (
        <ShipmentTrackingDetails
          data={trackQuery.data.data}
          shipmentId={shipmentId}
          reset={reset}
        />
      ) : (
        <TrackShipmentStart
          trackingId={trackingId}
          onTrackingIdChange={(value) => {
            setTrackingId(value);
            setInitiateTracking(false);
            if (localError) setLocalError(null);
          }}
          onTrack={handleTrack}
          isLoading={trackQuery.isFetching}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
}
